import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { confirmLargeArchive } from './confirmLargeArchive'
import { archiveErrorMessage } from './messages'
import { openDocumentationArchive, type OpenedDocumentation } from './openArchive'
import { useLatestRequest } from './useLatest'
import { buildPointsOffThread, decodeHeaderOffThread } from './workerClient'

export type LoadedArchive = OpenedDocumentation & { id: number }

type Status = { text: string; error: boolean }
type Screen = { archive: LoadedArchive | null; dropOpen: boolean }

const ARCHIVE_RE = /\.(7z|zip)$/i

export const useArchive = () => {
  const { t } = useTranslation('documentation')
  const request = useLatestRequest()
  const [screen, setScreen] = useState<Screen>({ archive: null, dropOpen: true })
  const [status, setStatus] = useState<Status>({ text: '', error: false })

  const open = useCallback(
    async (file: File) => {
      const isCurrent = request.start()
      if (!(await confirmLargeArchive(file, t)) || !isCurrent()) return
      setStatus({ text: t('readingIndex', { name: file.name }), error: false })
      try {
        const opened = await openDocumentationArchive(file, {
          decodeHeader: decodeHeaderOffThread,
          buildPoints: buildPointsOffThread,
        })
        if (!isCurrent()) return
        setScreen(previous => ({ archive: { ...opened, id: (previous.archive?.id ?? 0) + 1 }, dropOpen: false }))
        setStatus({ text: t('indexed', { name: file.name, count: opened.imageCount }), error: false })
      } catch (error) {
        if (isCurrent()) setStatus({ text: archiveErrorMessage(t, error, 'openFailed'), error: true })
      }
    },
    [request, t]
  )

  const openFirst = useCallback(
    (files: File[]) => {
      const file = files.find(item => ARCHIVE_RE.test(item.name))
      if (file) void open(file)
      else if (files.length) setStatus({ text: t('notArchive', { name: files[0].name }), error: true })
    },
    [open, t]
  )

  const showDrop = useCallback(() => setScreen(previous => ({ ...previous, dropOpen: true })), [])
  const hideDrop = useCallback(() => setScreen(previous => ({ ...previous, dropOpen: previous.archive == null })), [])

  return {
    archive: screen.archive,
    dropOpen: screen.dropOpen || screen.archive == null,
    status,
    openFirst,
    showDrop,
    hideDrop,
  }
}
